import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Crosshair, Users, BadgePercent, CheckCircle2 } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const BENEFITS = [
  { icon: Crosshair, text: "Range bays with priority lane reservations — $28/day (not hourly) on day of shooting" },
  { icon: Users, text: "Instruction from first shots to long-range precision" },
  { icon: BadgePercent, text: "Member pricing across the entire club store" },
  { icon: ShieldCheck, text: "A fellowship that trains hard and prays harder" },
];

const TIERS = [
  { id: "Standard Member", price: "$60/yr", note: "Full range access & store pricing" },
  { id: "Tactical Squad", price: "$120/yr", note: "Adds advanced courses & squad patches" },
  { id: "Benefactor Sponsor", price: "$300/yr", note: "Funds youth programs & benevolence" },
];

const inputCls = "mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none";

export default function Join() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "", statement: "", tier: "Standard Member" });
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please affirm the Code of Honor to continue");
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, affirmed: agree });
      setDone(true);
      toast.success("Application received. Welcome to the threshold, brother/sister.");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-16" data-testid="join-page">
      <section className="relative overflow-hidden border-b border-gold/15 noise-overlay crosshair-grid">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <p className="eyebrow mb-5">Join Now</p>
          <MaskedLines
            lines={["TAKE UP", "THE SHIELD."]}
            lineClassName="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-parchment"
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <h2 className="font-display text-2xl uppercase tracking-wide text-parchment">What membership carries</h2>
          <div className="mt-8 space-y-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex gap-4 items-start" data-testid={`benefit-${i}`}>
                <b.icon className="w-6 h-6 text-gold shrink-0 mt-0.5" />
                <p className="text-stone-300 text-sm sm:text-base leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 space-y-3">
            {TIERS.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`tier-${t.id.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => setForm((f) => ({ ...f, tier: t.id }))}
                className={`w-full text-left border px-5 py-4 transition-[border-color,box-shadow,background-color] duration-300 ${
                  form.tier === t.id
                    ? t.id === "Benefactor Sponsor"
                      ? "border-crimson bg-crimson/10 shadow-[0_0_15px_rgba(178,34,34,0.3)]"
                      : "border-gold bg-gold/10"
                    : "border-gold/20 bg-card2/40 hover:border-gold/50"
                }`}
              >
                <div className="flex justify-between items-baseline">
                  <span className="font-condensed uppercase tracking-wider text-lg text-parchment">{t.id}</span>
                  <span className="font-mono text-gold text-sm">{t.price}</span>
                </div>
                <p className="text-xs text-stone-400 mt-1">{t.note}</p>
              </button>
            ))}
          </div>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
            Applications reviewed by the board within 7 days. Dues collected after approval.
          </p>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div className="border border-gold/30 bg-card2/60 noise-overlay p-10 sm:p-14 text-center" data-testid="application-success">
              <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
              <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-parchment mt-6">Application Received</h2>
              <p className="mt-5 text-stone-300 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                Thank you, <span className="text-gold">{form.name}</span>. A chapter officer will reach out
                at <span className="text-gold">{form.email}</span> within one week to schedule your
                orientation and range safety walkthrough.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/store" data-testid="success-store-link" className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold px-7 py-3.5 hover:bg-gold-light transition-colors">Visit the Armory</Link>
                <Link to="/login" data-testid="success-login-link" className="border border-gold/40 font-condensed uppercase tracking-[0.2em] px-7 py-3.5 hover:border-gold hover:text-gold transition-colors">Create Member Login</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-gold/25 bg-card2/50 noise-overlay p-7 sm:p-10 space-y-6" data-testid="join-form">
              <h2 className="font-display text-xl uppercase tracking-wide text-parchment">Membership Application</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Full Name</Label>
                  <Input data-testid="join-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="John Knox" />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Email</Label>
                  <Input data-testid="join-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Phone (optional)</Label>
                  <Input data-testid="join-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="(555) 010-1441" />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Experience Level</Label>
                  <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })} required>
                    <SelectTrigger data-testid="join-experience-select" className={`${inputCls} rounded-none`}>
                      <SelectValue placeholder="Select your level" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate2 border-gold/25 text-parchment">
                      <SelectItem value="New to firearms">New to firearms</SelectItem>
                      <SelectItem value="Recreational shooter">Recreational shooter</SelectItem>
                      <SelectItem value="Experienced / competitor">Experienced / competitor</SelectItem>
                      <SelectItem value="Military or law enforcement">Military or law enforcement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Statement of Character &amp; Faith</Label>
                <Textarea
                  data-testid="join-statement-input"
                  required
                  rows={5}
                  value={form.statement}
                  onChange={(e) => setForm({ ...form, statement: e.target.value })}
                  className={inputCls}
                  placeholder="Tell us briefly about your faith walk and why you want to train with the order..."
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="join-agree-checkbox">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#D4AF37] w-4 h-4" />
                <span className="text-sm text-stone-300 leading-relaxed">
                  I have read and affirm the <Link to="/about" className="text-gold underline underline-offset-4">Code of Honor</Link> and
                  the <Link to="/bylaws" className="text-gold underline underline-offset-4">Bylaws of the Order</Link>,
                  and I understand that safety violations are grounds for immediate dismissal.
                </span>
              </label>
              <button
                type="submit"
                data-testid="join-submit-button"
                disabled={submitting}
                className="w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold py-4 hover:bg-gold-light hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-[background-color,box-shadow] duration-300 disabled:opacity-60"
              >
                {submitting ? "Submitting..." : `Submit Application — ${form.tier}`}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </main>
  );
}
